import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { submitLearningFeedback, clearSubmitLearningFeedback } from "../../redux/LearningFeedback/learningFeedbackSlice";
import {
  selectSubmitLearningFeedbackLoading,
  selectSubmitLearningFeedbackError,
} from "../../redux/LearningFeedback/learningFeedbackSelectors";
import CommonPhoneInput from "../../common/CommonPhoneInput/CommonPhoneInput";
import ToolManagmentCard from "../../common/element/Card/ToolManagmnetCard";

const QUESTIONS = [
  "What are your learning(s) & takeaways from today's session? What value did you get?",
  "How you are going to use this learning in your life?",
  "Did you get any realization / wow moment or breakthrough in this session? If yes please elaborate.",
  "How did you feel after the session? Describe your feelings.",
  "What do you want to say about the coach in reference to this particular coaching session or in general?",
];

const buildForm = (data) => ({
  participant_name: data?.participant_name || "",
  contact_number:   data?.contact_number  || "",
  company_name:     data?.company_name    || "",
  email:            data?.email_address   || data?.email || "",
  answers: [
    data?.learnings_and_takeaways   || "",
    data?.how_to_use_learning       || "",
    data?.realization_or_wow_moment || "",
    data?.feelings_after_session    || "",
    data?.coach_feedback            || "",
  ],
});

const FeedbackForm = ({ viewMode = false, initialData = null, onClose }) => {
  const dispatch = useDispatch();
  const loading = useSelector(selectSubmitLearningFeedbackLoading);
  const submitError = useSelector(selectSubmitLearningFeedbackError);

  const [form, setForm] = useState(() => viewMode ? buildForm(initialData) : buildForm(null));
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (viewMode && initialData) {
      setForm(buildForm(initialData));
    }
  }, [initialData, viewMode]);

  const validate = () => {
    const newErrors = {};
    if (!form.participant_name.trim()) newErrors.participant_name = "Participant name is required";
    if (!form.contact_number.trim()) newErrors.contact_number = "Contact number is required";
    if (!form.company_name.trim()) newErrors.company_name = "Company name is required";
    if (!form.email.trim()) newErrors.email = "Email address is required";
    else if (!/\S+@\S+\.\S+/.test(form.email)) newErrors.email = "Enter a valid email address";
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handlePhoneChange = (phone) => {
    setForm((prev) => ({ ...prev, contact_number: phone }));
    if (errors.contact_number) setErrors((prev) => ({ ...prev, contact_number: "" }));
  };

  const handleAnswerChange = (index, value) => {
    setForm((prev) => {
      const answers = [...prev.answers];
      answers[index] = value;
      return { ...prev, answers };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const payload = {
      participant_name:        form.participant_name,
      contact_number:          form.contact_number,
      company_name:            form.company_name,
      email_address:           form.email,
      learnings_and_takeaways: form.answers[0],
      how_to_use_learning:     form.answers[1],
      realization_or_wow_moment: form.answers[2],
      feelings_after_session:  form.answers[3],
      coach_feedback:          form.answers[4],
    };

    const result = await dispatch(submitLearningFeedback(payload));
    if (!result.error) {
      setSubmitted(true);
      dispatch(clearSubmitLearningFeedback());
    }
  };

  if (!viewMode && submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-10 max-w-lg w-full text-center">
          <div className="flex justify-center mb-4">
            <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
              <circle cx="32" cy="32" r="32" fill="#FFF3E0" />
              <path d="M20 32L28 40L44 24" stroke="#FF8E1C" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Thank You!</h2>
          <p className="text-gray-500">Your feedback has been submitted successfully.</p>
        </div>
      </div>
    );
  }

  const inputClass = (hasError) =>
    `w-full border input-shadow rounded-lg px-4 py-2 h-9 text-gray-700 outline-none transition
     ${viewMode ? "text-xs" : "text-sm"}
     ${hasError ? "border-red-400" : "border-gray-300"}
     ${viewMode ? "bg-gray-50 cursor-default focus:border-gray-300" : "focus:border-[#FF8E1C]"}`;

  const content = (
    <>
      {/* Header */}
      <div className="text-center bg-white rounded-xl py-6 px-4 sm:px-6">
        <h2 className={`font-bold text-black ${viewMode ? "text-sm sm:text-base md:text-lg" : "text-base sm:text-lg md:text-xl"}`}>
          Learning &amp; Takeaways from Today's Coaching Session
        </h2>
        <p className={`font-bold ${viewMode ? "text-sm sm:text-base md:text-lg" : "text-base sm:text-lg md:text-xl"}`}>
          <span className="text-black">From </span>
          <span className="text-[#FF8E1C]">Incredible Leaders Pvt. Ltd.</span>
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Participant info */}
        <div className="mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-start border-b pb-8">
            <div>
              <label className={`block text-gray-600 mb-1 ${viewMode ? "text-xs" : "text-sm"}`}>Participant Name</label>
              <input
                type="text"
                name="participant_name"
                placeholder="Participant Name"
                value={form.participant_name}
                onChange={handleChange}
                disabled={viewMode}
                className={inputClass(errors.participant_name)}
              />
              {!viewMode && errors.participant_name && (
                <p className="text-red-500 text-sm mt-1">{errors.participant_name}</p>
              )}
            </div>
            <div>
              <label className={`block text-gray-600 mb-1 ${viewMode ? "text-xs" : "text-sm"}`}>Contact Number</label>
              <div className="lf-phone-input">
                <CommonPhoneInput
                  value={form.contact_number}
                  onChange={handlePhoneChange}
                  placeholder="Contact Number"
                  disabled={viewMode}
                  error={!viewMode ? errors.contact_number : undefined}
                />
              </div>
            </div>
            <div>
              <label className={`block text-gray-600 mb-1 ${viewMode ? "text-xs" : "text-sm"}`}>Company Name</label>
              <input
                type="text"
                name="company_name"
                placeholder="Company Name"
                value={form.company_name}
                onChange={handleChange}
                disabled={viewMode}
                className={inputClass(errors.company_name)}
              />
              {!viewMode && errors.company_name && (
                <p className="text-red-500 text-sm mt-1">{errors.company_name}</p>
              )}
            </div>
            <div>
              <label className={`block text-gray-600 mb-1 ${viewMode ? "text-xs" : "text-sm"}`}>Email Address</label>
              <input
                type="email"
                name="email"
                placeholder="Email Address"
                value={form.email}
                onChange={handleChange}
                disabled={viewMode}
                className={inputClass(errors.email)}
              />
              {!viewMode && errors.email && (
                <p className="text-red-500 text-sm mt-1">{errors.email}</p>
              )}
            </div>
          </div>
        </div>

        {/* Questions */}
        <div className="border border-gray-200 rounded-lg p-4">
          {QUESTIONS.map((question, index) => (
            <div key={index} className="text-start p-1">
              <p className={`mb-2 font-bold leading-tight tracking-normal text-black font-['Lato',sans-serif] ${viewMode ? "text-xs sm:text-sm md:text-sm" : "text-xs sm:text-sm md:text-base"}`}>
                {index + 1}. {question}
              </p>
              <textarea
                rows={3}
                placeholder="Answer here..."
                value={form.answers[index]}
                onChange={(e) => handleAnswerChange(index, e.target.value)}
                disabled={viewMode}
                className={`w-full border border-gray-200 rounded-lg px-4 py-3 text-gray-700 outline-none resize-y transition
                  ${viewMode ? "text-xs bg-gray-50 cursor-default focus:border-gray-200" : "text-sm focus:border-[#FF8E1C]"}`}
              />
            </div>
          ))}
        </div>

        {!viewMode && submitError && (
          <p className="text-red-500 text-base mt-4 text-center">{submitError}</p>
        )}

        {/* Footer button */}
        <div className="flex justify-center mt-6">
          {viewMode ? (
            <button
              type="button"
              onClick={onClose}
              className="bg-[#FF8E1C] text-white font-semibold px-12 py-3 rounded-lg hover:bg-orange-500 transition text-base"
            >
              Close
            </button>
          ) : (
            <button
              type="submit"
              disabled={loading}
              className="bg-[#FF8E1C] text-white font-semibold px-12 py-3 rounded-lg hover:bg-orange-500 transition disabled:opacity-60 text-base"
            >
              {loading ? "Submitting..." : "Submit"}
            </button>
          )}
        </div>
      </form>
    </>
  );

  if (viewMode) {
    return <ToolManagmentCard>{content}</ToolManagmentCard>;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-4 px-3 sm:px-6">
      <div className="w-full max-w-full sm:max-w-[90%] md:max-w-[80%] lg:max-w-[75%] mx-auto bg-white rounded-xl shadow-md p-4 sm:p-6 md:p-8">
        {content}
      </div>
    </div>
  );
};

export default FeedbackForm;
